import {
  attachIdentityToActivity,
  ensureDirectLineOnline,
  getDirectLine,
  isDirectLineHealthy,
  isDirectLineTerminal,
} from "../directLlineClient";
import { acquireDelegatedGraphToken, getCurrentMsalAccount } from "../utils/msalTokenService";
import { fetchResultsPending, fetchResultsRejected } from "../ResultsSlice_Store";
import { selectCurrentProjectType } from "../ProjectSlice_Store";
import { ALLOWED_THRESHOLD_KEYS, THRESHOLD_DEFAULTS } from "../constants/thresholdDefaults";
import { addMessage, lockChatInput, setBotTyping, unlockChatInput } from "./chatSlice";
import { reconnectDirectLine } from "./chatConnectionThunks";
import type { ChatDispatch } from "./chatSliceTypes";

const AUTH_ERROR = "Authentication error. Please refresh the page and try again.";
const CONNECTION_ERROR = "Connection lost. Please refresh the page.";
const SEND_ERROR = "Error: Failed to send message.";

const ALLOWED_PROFILES = ["default", "flash"];
const ALLOWED_PARAMS = ["vector_similarity_weight", "similarity_threshold", "keyword"];

const toNumber = (value: unknown): number => (typeof value === "number" ? value : Number(value));

/**
 * Surface a send failure to the user. Control messages are invisible traffic —
 * they must never unlock the composer or paint an error over real results.
 */
function failSend(dispatch: ChatDispatch, isControlMessage: boolean, message: string): void {
  if (isControlMessage) return;
  dispatch(setBotTyping(false));
  dispatch(fetchResultsRejected(message));
  dispatch(unlockChatInput());
}

/**
 * Pre-send health check. A terminal connection (Ended/ExpiredToken/
 * FailedToConnect) needs the full reconnect cycle; a merely transient one just
 * needs waiting out.
 */
async function ensureConnection(dispatch: ChatDispatch): Promise<void> {
  if (isDirectLineHealthy()) return;

  if (isDirectLineTerminal()) {
    await dispatch(reconnectDirectLine());
  } else {
    await ensureDirectLineOnline();
  }
}

/** Read a gated JSON admin setting, treating any read/parse failure as unset. */
function readJsonSetting(enabledKey: string, valueKey: string): Record<string, any> | null {
  try {
    if (localStorage.getItem(enabledKey) !== "true") return null;

    const stored = localStorage.getItem(valueKey);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    // Ignore localStorage read/parse errors
    return null;
  }
}

/** Admin threshold overrides, minus any value that just restates the default. */
function buildThresholdOverrides(): Record<string, number> | null {
  const overrides = readJsonSetting(
    "dealos.thresholdOverridesEnabled",
    "dealos.thresholdOverrides"
  );
  if (!overrides) return null;

  const nonDefaultOverrides: Record<string, number> = {};
  for (const key of ALLOWED_THRESHOLD_KEYS) {
    if (!(key in overrides)) continue;

    const numericValue = toNumber(overrides[key]);
    if (!Number.isFinite(numericValue)) continue;
    if (key === "page_size" && !Number.isInteger(numericValue)) continue;
    if (numericValue !== THRESHOLD_DEFAULTS[key]) {
      nonDefaultOverrides[key] = numericValue;
    }
  }

  return Object.keys(nonDefaultOverrides).length > 0 ? nonDefaultOverrides : null;
}

/** One search profile: `keyword` is a boolean, every other param a 0-1 number. */
function validateSearchProfile(profileVal: Record<string, unknown>): Record<string, number | boolean> {
  const validatedProfile: Record<string, number | boolean> = {};

  for (const pkey of ALLOWED_PARAMS) {
    if (!(pkey in profileVal)) continue;

    const raw = profileVal[pkey];
    if (pkey === "keyword") {
      if (typeof raw === "boolean") validatedProfile[pkey] = raw;
      continue;
    }

    const num = toNumber(raw);
    if (!Number.isFinite(num) || num < 0 || num > 1) continue;
    validatedProfile[pkey] = num;
  }

  return validatedProfile;
}

/**
 * Per-category search overrides from admin settings.
 * Stored shape: {"default": {"vector_similarity_weight": 0.4, ...}, "flash": {...}}
 */
function buildSearchOverrides(): Record<string, Record<string, number | boolean>> | null {
  const parsed = readJsonSetting("dealos.searchOverridesEnabled", "dealos.searchOverrides");
  if (!parsed) return null;

  const validated: Record<string, Record<string, number | boolean>> = {};
  for (const profile of ALLOWED_PROFILES) {
    const profileVal = parsed[profile];
    if (!profileVal || typeof profileVal !== "object") continue;

    const validatedProfile = validateSearchProfile(profileVal);
    if (Object.keys(validatedProfile).length > 0) {
      validated[profile] = validatedProfile;
    }
  }

  return Object.keys(validated).length > 0 ? validated : null;
}

/** Everything the core agent needs alongside the message text. */
function buildChannelData(
  state: any,
  projectId: string,
  isControlMessage: boolean
): Record<string, any> {
  const { mcpEnvironment, dataVersion, selectedUseCase } = state.chatSlice;
  const { userName, userEmail } = state.userAuthAndAdminSlice;

  const channelData: Record<string, any> = {
    mcp_environment: mcpEnvironment,
    data_version: dataVersion,
    message_type: isControlMessage ? "control" : "user",
    username: userName || userEmail,
    project_id: projectId,
    // selectedUseCase for a new project, project type for a reloaded one
    use_case: selectedUseCase || selectCurrentProjectType(state),
  };

  const thresholdOverrides = buildThresholdOverrides();
  if (thresholdOverrides) {
    channelData.threshold_overrides = thresholdOverrides;
  }

  const searchOverrides = buildSearchOverrides();
  if (searchOverrides) {
    channelData.search_overrides = searchOverrides;
  }

  return channelData;
}

/**
 * Phase 1: refresh the passport, then stamp the activity with a delegated Graph
 * token. Returns null when either step fails — we never send unidentified.
 */
async function withIdentity(activity: any): Promise<any> {
  try {
    const { ensureValidPassport } = await import("../directLlineClient");
    await ensureValidPassport();
  } catch (passportError) {
    console.error(
      "❌ Failed to ensure valid passport — cannot send without valid auth:",
      passportError
    );
    return null;
  }

  try {
    const delegatedToken = await acquireDelegatedGraphToken();
    const identified = attachIdentityToActivity(activity, delegatedToken);
    console.log("✅ Attached user identity to activity");
    return identified;
  } catch (tokenError) {
    console.error("❌ Failed to acquire delegated token — cannot send without identity:", tokenError);
    return null;
  }
}

/** Post the activity; a subscribe-time error reports exactly like a throw. */
async function postMessage(
  text: string,
  channelData: Record<string, any>,
  dispatch: ChatDispatch,
  isControlMessage: boolean
): Promise<void> {
  const msalAccount = getCurrentMsalAccount();
  const directLine = await getDirectLine(null, msalAccount);

  const activity = await withIdentity({
    from: { role: "user" },
    type: "message",
    text,
    channelData,
  });
  if (!activity) {
    failSend(dispatch, isControlMessage, AUTH_ERROR);
    return;
  }

  directLine.postActivity(activity).subscribe({
    error: () => failSend(dispatch, isControlMessage, SEND_ERROR),
  });
}

/** Send a user message (or a CONTROL_ message) to the core agent. */
export const sendMessage =
  (text: string) =>
  async (dispatch: any, getState: any): Promise<void> => {
    const isControlMessage = text.startsWith("CONTROL_");
    const currentProjectId = getState().projectSlice?.currentProjectId;

    if (!currentProjectId && !isControlMessage) return;
    // Short-circuit duplicate submits for non-control messages
    if (!isControlMessage && !getState().chatSlice.chatInputReady) return;

    if (!isControlMessage) {
      dispatch(lockChatInput());
      dispatch(addMessage({ sender: "user", text }));
      dispatch(setBotTyping(true));
      // Show the loading state in the results panel
      dispatch(fetchResultsPending());
    }

    // Re-pull state in case the dispatches above updated anything.
    const state = getState();
    const projectId = state.projectSlice.currentProjectId || currentProjectId;
    if (!projectId && !isControlMessage) return;

    const channelData = buildChannelData(state, projectId, isControlMessage);

    try {
      await ensureConnection(dispatch);
    } catch {
      failSend(dispatch, isControlMessage, CONNECTION_ERROR);
      return;
    }

    try {
      await postMessage(text, channelData, dispatch, isControlMessage);
    } catch (err) {
      failSend(dispatch, isControlMessage, (err as Error).message || "Unknown error");
    }
  };
